import { Edit2, Eye, UserIcon } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardFilterBar, {
  ALL_OPTION,
  isAllFilter,
  stringFromFilter,
} from "../../components/ui/dashboard/DashboardFilterBar";
import Pagination from "../../components/ui/dashboard/Pagination";
import Table, { Column } from "../../components/ui/dashboard/Table";
import ThreeDotsMenu from "../../components/ui/ThreeDotsMenu";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { buildApiUrl, formatDate } from "../../lib/utils";
import { FuelLog, FuelLogSearchRequest, FuelLogSearchResponse } from "./@types";
import ExportFuelLogModal from "./components/ExportFuelLogModal";
import { useGetFuelLogsMutation } from "./fuelLogApiSlice";
import FuelLogDashStats from "./refactor/FuelLogDashStats";

const FUEL_TYPE_OPTIONS = [
  ALL_OPTION,
  { value: "Petrol", label: "Petrol" },
  { value: "Diesel", label: "Diesel" },
];

const FuelLogDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [fuelType, setFuelType] = useState("all");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [getFuelLog, { data: maintenanceData, isLoading, error: loadError }] =
    useGetFuelLogsMutation();
  const debouncedKeyword = useDebouncedValue(searchTerm.trim());

  const fetchFuelLogs = useCallback(async () => {
    const requestBody: FuelLogSearchRequest = {
      pageNumber: currentPage,
      pageSize,
      keyword: debouncedKeyword || undefined,
      fuelType: stringFromFilter(fuelType),
    };

    try {
      await getFuelLog(requestBody).unwrap();
    } catch (err) {
      console.error("Failed to fetch fuel logs:", err);
    }
  }, [currentPage, pageSize, debouncedKeyword, fuelType, getFuelLog]);

  useEffect(() => {
    fetchFuelLogs();
  }, [fetchFuelLogs]);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedKeyword, fuelType]);

  // Table columns configuration
  const columns: Column<FuelLog>[] = [
    {
      key: "tripNumber",
      header: "Trip number",
      width: "200px",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "createdOn",
      header: "Date",
      width: "20",
      render: (value: string) => (
        <span className="text-hColor">{formatDate(value)}</span>
      ),
    },
    {
      key: "vehicleModel",
      header: "Vehicle",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "driverName",
      header: "Driver",
      width: "",
      render: (value: string, item: FuelLog) => (
        <div className="flex items-center space-x-3">
          {item.driverImage ? (
            <img
              src={buildApiUrl(item.driverImage)}
              alt={`${item.driverName}`}
              className="w-[30px] h-[30px] rounded-full object-cover border border-gray-300 shadow-sm"
            />
          ) : (
            <div className="w-[30px] h-[30px] rounded-full bg-gray-200 flex items-center justify-center border border-gray-300 shadow-sm p-1">
              <UserIcon className="w-full h-full text-hColor" />
            </div>
          )}
          <div className="flex flex-col">
            <span className="text-hColor capitalize">{item.driverName}</span>
          </div>
        </div>
      ),
    },
    {
      key: "fuelType",
      header: "FuelLog type",
      width: "20",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "fuelCost",
      header: "Fuel cost (NGN)",
      width: "16",
      render: (value: string) => <span className="text-hColor">{value}</span>,
    },
    {
      key: "fuelPurchased",
      header: "Fuel consumed",
      width: "16",
      render: (value: string) => (
        <span className="text-hColor">{value} Litters</span>
      ),
    },
    {
      key: "actions",
      header: "Action",
      width: "12",
      align: "center" as const,
      render: (value: any, item: FuelLog) => {
        return (
          <ThreeDotsMenu
            actions={[
              {
                label: "View",
                onClick: () => navigate(`/admin/fuel/${item?.id}`),
                icon: <Eye className="w-4 h-4" />,
              },
              {
                label: "Edit",
                onClick: () => {},
                icon: <Edit2 className="w-4 h-4" />,
              },
            ]}
          />
        );
      },
    },
  ];

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];
    if (debouncedKeyword) {
      chips.push({
        key: "keyword",
        label: `Search: ${debouncedKeyword}`,
        onRemove: () => setSearchTerm(""),
      });
    }
    if (!isAllFilter(fuelType)) {
      chips.push({
        key: "fuel",
        label: `Fuel: ${fuelType}`,
        onRemove: () => setFuelType("all"),
      });
    }
    return chips;
  }, [debouncedKeyword, fuelType]);

  const response = maintenanceData?.data as FuelLogSearchResponse | undefined;

  return (
    <div className="min-h-screen py-6 w-full bg-[#F9F9F9] p-4">
      <div className="w-full mx-auto space-y-6">
        {/* Dashboard Cards */}
        <div className="">
          <FuelLogDashStats />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 bg-white rounded-[10px] p-4">
          <div className="">
            <h2 className="text-xl font-semibold text-hColor">All fuel logs</h2>
          </div>

          <div className="flex gap-3">
            <ExportFuelLogModal />
          </div>
        </div>

        {/* All fuel logs section */}
        <div className="space-y-4 bg-white rounded-[10px] p-4">
          <DashboardFilterBar
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            onClearSearch={() => setSearchTerm("")}
            searchPlaceholder="Search by trip, vehicle or driver"
            isLoading={isLoading}
            filters={[
              {
                id: "fuel-type",
                placeholder: "Fuel type",
                value: fuelType,
                options: FUEL_TYPE_OPTIONS,
                onChange: (value) => setFuelType(value || "all"),
              },
            ]}
            chips={activeFilters}
            onClearAll={() => {
              setSearchTerm("");
              setFuelType("all");
            }}
            resultCount={response?.totalCount}
            resultLabel={response?.totalCount === 1 ? "fuel log" : "fuel logs"}
          />

          {/* Table */}
          <div className="overflow-hidden space-y-6">
            <Table
              data={response?.data || []}
              columns={columns}
              loading={isLoading}
              error={loadError}
              emptyMessage="No fuel logs found"
            />

            {/* Pagination */}
            {response && (
              <Pagination
                currentPage={response.currentPage}
                totalPages={response.totalPages}
                pageSize={response.pageSize}
                totalCount={response.totalCount}
                hasNextPage={response.hasNextPage}
                hasPreviousPage={response.hasPreviousPage}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
              />
            )}
          </div>
        </div>
      </div>

      {/* {openModal && (
        <MaintenanceModal
          isOpen={openModal}
          onClose={() => handleModal()}
          maintenance={selectedFuelLog ? selectedFuelLog : null}
        />
      )} */}
    </div>
  );
};

export default FuelLogDashboard;
